import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { PanelList } from "@/components/ui/PanelList";
import { PanelListItem } from "@/components/ui/PanelListItem";
import { EditIcon, SpinnerIcon, UsersIcon } from "@/components/icons";
import { useAppStore } from "@/core/store/useAppStore";
import { WorkspacePanel } from "@/domains/Ideas/components/WorkspacePanel";
import { teamMembers } from "@/domains/Client/agencyPeople";
import type { AgencyPerson } from "@/domains/Client/agencyPeople.service";
import {
  NAME_MAX_LENGTH,
  NAME_REQUIRED_MESSAGE,
  nameEdit,
  renameErrorMessage,
} from "@/domains/Client/personName";
import {
  useAgencyPeopleQuery,
  useRenamePersonMutation,
} from "@/domains/Client/useAgencyPeople";

const displayName = (person: AgencyPerson): string => person.name || person.email;

interface EditPersonNameModalProps {
  person: AgencyPerson | null;
  onClose: () => void;
  onSave: (name: string) => Promise<void>;
}

/** "Modifica nome": one name field in a centered modal (DD-006). The name
 * is global, so it changes on every client and task (TD-086). */
function EditPersonNameModal({ person, onClose, onSave }: EditPersonNameModalProps) {
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setName(person?.name ?? "");
    setError(null);
  }, [person]);

  const edit = nameEdit(person?.name ?? null, name);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (edit.kind !== "rename") return;
    setIsSaving(true);
    setError(null);
    try {
      await onSave(edit.name);
      onClose();
    } catch (err) {
      setError(renameErrorMessage(err, "Impossibile aggiornare il nome."));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={person !== null}
      onClose={() => {
        if (!isSaving) onClose();
      }}
      title="Modifica nome"
      size="sm"
      bodyPadding
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1">
          <label htmlFor="team-person-name-input" className="text-sm font-medium text-gray-900">
            Nome
          </label>
          <input
            id="team-person-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={NAME_MAX_LENGTH}
            placeholder="es. Marco Rossi"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden text-sm"
            disabled={isSaving}
            autoFocus
          />
          <p className="text-xs text-gray-500">{person?.email}</p>
        </div>

        {(edit.kind === "blank" || error) && (
          <p className="text-xs text-red-600">
            {edit.kind === "blank" ? NAME_REQUIRED_MESSAGE : error}
          </p>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={isSaving || edit.kind !== "rename"}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving && <SpinnerIcon className="w-4 h-4" />}
            Salva
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Every agency member (DD-047). Members come from the ops script, so the
 * panel has no add action. Contacts are named in each client's access
 * modal instead.
 */
export function TeamPanel() {
  const [collapsed, setCollapsed] = useState(false);
  const [editing, setEditing] = useState<AgencyPerson | null>(null);
  const { data, isLoading, isError } = useAgencyPeopleQuery();
  const renamePerson = useRenamePersonMutation();
  const addToast = useAppStore((state) => state.addToast);

  const members = useMemo(() => teamMembers(data ?? []), [data]);
  const summary = data
    ? `${members.length} ${members.length === 1 ? "persona" : "persone"}`
    : undefined;

  const handleSave = async (name: string) => {
    if (!editing) return;
    await renamePerson.mutateAsync({ personId: editing.id, name });
    addToast({ message: "Nome aggiornato", type: "success", duration: 3000 });
  };

  return (
    <>
      <WorkspacePanel
        title="Team"
        icon={<UsersIcon className="w-4 h-4" />}
        summary={summary}
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((c) => !c)}
        className={collapsed ? "" : "h-full"}
        loading={isLoading}
      >
        {isError ? (
          <p className="text-sm text-red-600">Impossibile caricare il team.</p>
        ) : (
          <PanelList
            items={members}
            keyFor={(person) => person.id}
            empty={
              <div className="text-center py-6">
                <UsersIcon className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                <p className="text-sm font-medium text-gray-900">Nessun membro nel team</p>
                <p className="text-sm text-gray-500 mt-1">
                  I membri si aggiungono con lo script di amministrazione.
                </p>
              </div>
            }
            renderItem={(person) => (
              <PanelListItem
                title={displayName(person)}
                meta={
                  person.name ? (
                    <span className="text-xs text-gray-500 truncate">{person.email}</span>
                  ) : undefined
                }
                trailing={
                  <button
                    type="button"
                    onClick={() => setEditing(person)}
                    className="p-1.5 text-gray-400 hover:text-gray-700"
                    title="Modifica nome"
                    aria-label={`Modifica nome di ${displayName(person)}`}
                  >
                    <EditIcon className="w-4 h-4" />
                  </button>
                }
              />
            )}
          />
        )}
      </WorkspacePanel>

      <EditPersonNameModal
        person={editing}
        onClose={() => setEditing(null)}
        onSave={handleSave}
      />
    </>
  );
}
